import { useCallback, useEffect, useRef, useState, type RefObject } from "react"
import type { NavigateFunction, To } from "react-router-dom"

import type { GeocodeV4Response } from "@be-miniapp/api"

import type { RideLocation } from "../stores/ride-data"
import { useRideOptionFormState } from "../stores/ride-option-form-state"

export type ConfirmAddressStandardLocation = Pick<
  RideLocation,
  "name" | "address" | "placeId" | "location"
>

/** Mapper differs slightly between AppView and Zalo geocode utils — inject from the app. */
export type ConfirmAddressFromGeocodeFn = (
  geocode: GeocodeV4Response
) => ConfirmAddressStandardLocation

/**
 * True when pickup coordinates differ from the geocoded pin (used to avoid clobbering pickup on confirm).
 * Replaces an incorrect lat-vs-lng cross-compare that existed in both apps.
 */
export function isPickupLocationChangedByGeocode(
  pickup: RideLocation | undefined,
  standardLocation: ConfirmAddressStandardLocation
): boolean {
  const lat = standardLocation.location?.latitude
  const lng = standardLocation.location?.longitude
  if (lat === undefined || lng === undefined) return false
  return (
    pickup?.location?.latitude !== lat || pickup?.location?.longitude !== lng
  )
}

export interface ConfirmAddressPaneModelReturn {
  note: string
  tempNote: string
  setTempNote: (v: string) => void
  isNoteOpen: boolean
  onNoteClose: () => void
  openNoteSheet: () => void
  onConfirm: () => void
  pointData: ConfirmAddressStandardLocation | undefined
  inputNoteRef: RefObject<HTMLInputElement | null>
  onSaveNote: () => void
  goEditPickup: () => void
  isLoading: boolean
}

export function useConfirmAddressPaneModel(options: {
  currentPoint: GeocodeV4Response | undefined
  isLoading: boolean
  fromGeocodeLocationToStandardLocation: ConfirmAddressFromGeocodeFn
  navigate: NavigateFunction
  pickup: RideLocation | undefined
  setPickup: (pickup: RideLocation) => void
  setNote: (note: string | undefined) => void
  bookingPath?: To
}): ConfirmAddressPaneModelReturn {
  const {
    currentPoint,
    isLoading,
    fromGeocodeLocationToStandardLocation,
    navigate,
    pickup,
    setPickup,
    setNote,
    bookingPath = "/ride/booking",
  } = options

  const formState = useRideOptionFormState()

  const [note, setCurrentNote] = useState("")
  const [isNoteOpen, setIsNoteOpen] = useState(false)
  const [tempNote, setTempNote] = useState("")
  const inputNoteRef = useRef<HTMLInputElement | null>(null)

  const onNoteClose = useCallback(() => setIsNoteOpen(false), [])

  useEffect(() => {
    if (!isNoteOpen) return
    const id = window.setTimeout(() => inputNoteRef.current?.focus(), 100)
    return () => window.clearTimeout(id)
  }, [isNoteOpen])

  const onConfirm = useCallback(() => {
    if (!currentPoint) return

    const standardLocation = fromGeocodeLocationToStandardLocation(currentPoint)

    const isLocationDifferent = isPickupLocationChangedByGeocode(
      pickup,
      standardLocation
    )

    if (standardLocation.name && isLocationDifferent) {
      setPickup({
        type: "pick-up",
        ...standardLocation,
        isConfirmed: true,
      })
    }

    setNote(note)
    navigate(bookingPath)
  }, [
    bookingPath,
    currentPoint,
    fromGeocodeLocationToStandardLocation,
    navigate,
    note,
    pickup,
    setNote,
    setPickup,
  ])

  const pointData = currentPoint
    ? fromGeocodeLocationToStandardLocation(currentPoint)
    : undefined

  const openNoteSheet = useCallback(() => setIsNoteOpen(true), [])

  const onSaveNote = useCallback(() => {
    setCurrentNote(tempNote)
    formState.setNote(tempNote)
    onNoteClose()
  }, [formState, onNoteClose, tempNote])

  const goEditPickup = useCallback(
    () => navigate("/ride/edit-pickup"),
    [navigate]
  )

  return {
    note,
    tempNote,
    setTempNote,
    isNoteOpen,
    onNoteClose,
    openNoteSheet,
    onConfirm,
    pointData,
    inputNoteRef,
    onSaveNote,
    goEditPickup,
    isLoading,
  }
}
